<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
-->

<template>
  <div class="orangehrm-paper-container">
    <div class="orangehrm-header-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('expensa.cfg_vehicles') }}
      </oxd-text>
      <oxd-button
        v-if="canWrite"
        display-type="secondary"
        :label="$t('general.add')"
        @click="onAdd"
      />
    </div>
    <oxd-divider />
    <oxd-text
      v-if="state === 'loading'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.loading') }}
    </oxd-text>
    <oxd-text
      v-else-if="state === 'error'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.list_error') }}
    </oxd-text>
    <oxd-text
      v-else-if="state === 'empty'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.vehicles_empty') }}
    </oxd-text>
    <div v-else class="orangehrm-container">
      <oxd-card-table
        :items="rows"
        :headers="headers"
        :selectable="false"
        :clickable="false"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
  </div>

  <teleport to="#app">
    <!-- Creazione (con tariffa iniziale) e modifica del veicolo -->
    <oxd-dialog
      v-if="showForm"
      class="orangehrm-dialog-modal"
      @update:show="showForm = false"
    >
      <div class="orangehrm-modal-header">
        <oxd-text type="card-title">
          {{ editing ? $t('expensa.edit_vehicle') : $t('expensa.new_vehicle') }}
        </oxd-text>
      </div>
      <oxd-divider />
      <oxd-form :loading="saving" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-input-field
            v-model="form.name"
            :label="$t('general.name')"
            :rules="rules.name"
            required
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.plate"
                :label="$t('expensa.col_plate')"
                :rules="rules.plate"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="fuelOption"
                type="select"
                :label="$t('expensa.col_fuel')"
                :options="fuelOptions"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <template v-if="!editing">
          <oxd-form-row>
            <oxd-grid :cols="2" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="initialRate"
                  :label="$t('expensa.initial_rate')"
                  :rules="rules.rate"
                  required
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="rateSource"
                  :label="$t('expensa.rate_source')"
                  :rules="rules.source"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
        </template>
        <oxd-form-row v-else>
          <oxd-input-field
            v-model="form.active"
            type="checkbox"
            :true-value="true"
            :false-value="false"
            :option-label="$t('general.active')"
          />
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <oxd-button
            type="button"
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="showForm = false"
          />
          <oxd-button
            type="submit"
            display-type="secondary"
            :label="$t('general.save')"
            :disabled="saving"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-dialog>

    <!-- Storico delle tariffe e nuova tariffa dal giorno -->
    <oxd-dialog
      v-if="ratesFor"
      class="orangehrm-dialog-modal"
      @update:show="ratesFor = null"
    >
      <div class="orangehrm-modal-header">
        <oxd-text type="card-title">
          {{ $t('expensa.rates_history') }} - {{ ratesFor.name }}
        </oxd-text>
      </div>
      <oxd-divider />
      <oxd-text v-if="ratesState === 'error'">
        {{ $t('expensa.list_error') }}
      </oxd-text>
      <oxd-text v-else-if="ratesState === 'loading'">
        {{ $t('expensa.loading') }}
      </oxd-text>
      <oxd-text v-else-if="rates.length === 0">
        {{ $t('expensa.no_rates') }}
      </oxd-text>
      <div v-else class="orangehrm-container">
        <oxd-card-table
          :items="rateRows"
          :headers="rateHeaders"
          :selectable="false"
          :clickable="false"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <template v-if="canWrite">
        <oxd-divider />
        <oxd-form :loading="saving" @submit-valid="onAddRate">
          <oxd-form-row>
            <oxd-text type="card-title">{{ $t('expensa.new_rate') }}</oxd-text>
          </oxd-form-row>
          <oxd-form-row>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <date-input
                  v-model="newRate.validFrom"
                  :label="$t('expensa.valid_from')"
                  :rules="rules.validFrom"
                  required
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="newRate.ratePerKm"
                  :label="$t('expensa.col_rate')"
                  :rules="rules.rate"
                  required
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="newRate.source"
                  :label="$t('expensa.rate_source')"
                  :rules="rules.source"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
          <oxd-form-actions>
            <oxd-button
              type="submit"
              display-type="secondary"
              :label="$t('expensa.add_rate')"
              :disabled="saving"
            />
          </oxd-form-actions>
        </oxd-form>
      </template>
      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('expensa.close')"
          @click="ratesFor = null"
        />
      </oxd-form-actions>
    </oxd-dialog>
  </teleport>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {
  required,
  shouldNotExceedCharLength,
  validDateFormat,
} from '@/core/util/validation/rules';
import {expensaApi, errorMessage} from '../../util/api';
import {
  FUEL_TYPES,
  rateToApi,
  vehiclePatch,
  vehicleToForm,
  vehicleValues,
} from '../../util/configForms';
import {day} from '../../util/format';

const emptyVehicle = () => ({
  name: '',
  plate: '',
  fuelType: null,
  active: true,
});
const emptyRate = () => ({validFrom: null, ratePerKm: '', source: ''});

// Veicoli aziendali e tariffe al km. Per finance l'API restituisce anche i
// veicoli personali: la colonna "aziendale" li distingue.
export default {
  name: 'ExpensaVehiclesSection',
  components: {
    'oxd-dialog': OxdDialog,
  },
  props: {
    canWrite: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {userDateFormat} = useDateFormat();
    return {userDateFormat};
  },
  data() {
    return {
      state: 'loading',
      vehicles: [],
      showForm: false,
      editing: null,
      form: emptyVehicle(),
      initialRate: '',
      rateSource: '',
      saving: false,
      ratesFor: null,
      ratesState: 'loading',
      rates: [],
      newRate: emptyRate(),
    };
  },
  computed: {
    rows() {
      return this.vehicles.map((v) => ({
        id: v.id,
        name: v.name,
        plate: v.plate || '—',
        fuel: v.fuelType || '—',
        company: v.company ? this.$t('general.yes') : this.$t('general.no'),
        rate: v.ratePerKm ? String(v.ratePerKm).replace('.', ',') : '—',
        active: v.active ? this.$t('general.yes') : this.$t('general.no'),
      }));
    },
    headers() {
      const headers = [
        {name: 'name', title: this.$t('general.name'), style: {flex: 3}},
        {name: 'plate', title: this.$t('expensa.col_plate'), style: {flex: 1}},
        {name: 'fuel', title: this.$t('expensa.col_fuel'), style: {flex: 1}},
        {
          name: 'company',
          title: this.$t('expensa.col_company'),
          style: {flex: 1},
        },
        {
          name: 'rate',
          title: this.$t('expensa.col_current_rate'),
          style: {flex: 1},
        },
        {name: 'active', title: this.$t('general.active'), style: {flex: 1}},
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 2},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            ...(this.canWrite
              ? {edit: {onClick: this.onEdit, props: {name: 'pencil-fill'}}}
              : {}),
            rates: {
              onClick: this.onShowRates,
              component: 'oxd-button',
              props: {
                label: this.$t('expensa.col_rate'),
                displayType: 'text',
              },
            },
          },
        },
      ];
      return headers;
    },
    rateRows() {
      return this.rates.map((r) => ({
        id: r.id,
        validFrom: day(r.validFrom),
        rate: String(r.ratePerKm).replace('.', ','),
        source: r.source || '—',
      }));
    },
    rateHeaders() {
      return [
        {
          name: 'validFrom',
          title: this.$t('expensa.valid_from'),
          style: {flex: 1},
        },
        {name: 'rate', title: this.$t('expensa.col_rate'), style: {flex: 1}},
        {
          name: 'source',
          title: this.$t('expensa.rate_source'),
          style: {flex: 2},
        },
      ];
    },
    fuelOptions() {
      return [
        {id: null, label: '—'},
        ...FUEL_TYPES.map((f) => ({id: f, label: f})),
      ];
    },
    fuelOption: {
      get() {
        return this.fuelOptions.find((o) => o.id === this.form.fuelType);
      },
      set(option) {
        this.form.fuelType = option ? option.id : null;
      },
    },
    rules() {
      return {
        name: [required, shouldNotExceedCharLength(100)],
        plate: [shouldNotExceedCharLength(20)],
        source: [shouldNotExceedCharLength(100)],
        rate: [
          required,
          (v) => rateToApi(v) !== null || this.$t('expensa.set_invalid_rate'),
        ],
        validFrom: [required, validDateFormat(this.userDateFormat)],
      };
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const {data} = await expensaApi('/vehicles').getAll({
          includeInactive: true,
        });
        this.vehicles = data.data;
        this.state = this.vehicles.length === 0 ? 'empty' : 'list';
      } catch (e) {
        this.state = 'error';
      }
    },
    onAdd() {
      this.editing = null;
      this.form = emptyVehicle();
      this.initialRate = '';
      this.rateSource = '';
      this.showForm = true;
    },
    onEdit(item) {
      const vehicle = this.vehicles.find((v) => v.id === item.id);
      if (!vehicle) return;
      this.editing = vehicle;
      this.form = vehicleToForm(vehicle);
      this.showForm = true;
    },
    toastOk() {
      this.$toast.success({
        title: this.$t('general.success'),
        message: this.$t('expensa.toast_done'),
      });
    },
    toastError(e) {
      this.$toast.error({
        title: this.$t('general.error'),
        message: errorMessage(e),
      });
    },
    async onSave() {
      this.saving = true;
      try {
        if (this.editing) {
          const {patch, invalid} = vehiclePatch(this.editing, this.form);
          if (invalid.length > 0) throw new Error(invalid.join(','));
          if (Object.keys(patch).length > 0) {
            await expensaApi('/vehicles').update(this.editing.id, patch);
          }
        } else {
          const {values, invalid} = vehicleValues(this.form);
          const rate = rateToApi(this.initialRate);
          if (invalid.length > 0 || rate === null) {
            throw new Error(invalid.join(','));
          }
          const body = {
            name: values.name,
            company: true,
            ratePerKm: rate,
          };
          if (values.plate !== null) body.plate = values.plate;
          if (values.fuelType !== null) body.fuelType = values.fuelType;
          if (this.rateSource.trim() !== '') {
            body.rateSource = this.rateSource.trim();
          }
          await expensaApi('/vehicles').create(body);
        }
        this.toastOk();
        this.showForm = false;
        await this.load();
      } catch (e) {
        this.toastError(e);
      } finally {
        this.saving = false;
      }
    },
    async onShowRates(item) {
      const vehicle = this.vehicles.find((v) => v.id === item.id);
      if (!vehicle) return;
      this.ratesFor = vehicle;
      this.newRate = emptyRate();
      await this.loadRates();
    },
    async loadRates() {
      this.ratesState = 'loading';
      try {
        const {data} = await expensaApi(
          `/vehicles/${this.ratesFor.id}/rates`,
        ).getAll();
        this.rates = data.data;
        this.ratesState = 'list';
      } catch (e) {
        this.ratesState = 'error';
      }
    },
    async onAddRate() {
      this.saving = true;
      try {
        const body = {
          validFrom: this.newRate.validFrom,
          ratePerKm: rateToApi(this.newRate.ratePerKm),
        };
        if (this.newRate.source.trim() !== '') {
          body.source = this.newRate.source.trim();
        }
        await expensaApi(`/vehicles/${this.ratesFor.id}/rates`).create(body);
        this.toastOk();
        this.newRate = emptyRate();
        await Promise.all([this.loadRates(), this.load()]);
      } catch (e) {
        this.toastError(e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-empty {
  padding: 0 1.2rem 1.2rem;
}
</style>
